import React, { useEffect, useState } from 'react';
import { AuditLogEventName, StyledDivider } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Stack, Typography } from '@mui/material';
import { IAuditLogItemResponse } from 'models/auditLog';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import {
  ILicenceTemplateApplicationProcessDocument,
  ILicenceTemplateApplicationProcessResponse,
  ILicenceTemplateApplicationProcessTask,
} from 'models/licenceConfigurator';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useNavigate } from 'react-router-dom';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ERouteLinks } from 'models/route';
import { EAuditLogEvents } from '../helpers';
import { useLang } from 'models/langContext';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { findInDictionary } from 'helpers/dictionary';
import {
  ApplicationProcessDocumentTableColumns,
  ApplicationProcessTaskTableColumns,
  mapApplicationProcessDocument,
  mapApplicationProcessTask,
  statusApplicationProcessChipVariant,
} from 'pages/LicenceConfigurationPage/helper';
import { Chips } from 'components/atoms/Chips';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { priceFormat } from 'helpers/price';
import { Table } from 'components/molecules/Table';

interface ILicencesEventDetails {
  auditLog: IAuditLogItemResponse;
}

interface IRenderDetails {
  applicationProcess: ILicenceTemplateApplicationProcessResponse;
  documents?: ILicenceTemplateApplicationProcessDocument[];
  tasks?: ILicenceTemplateApplicationProcessTask[];
  t: any;
}
const RenderDetails = ({ applicationProcess, documents, tasks, t }: IRenderDetails) => {
  const { selectedLanguage } = useLang();
  const { currency } = useGlobalProperty();

  const {
    applicationProcessTypes,
    loadDictionaries,
    applicationProcessStatus,
    attachmentDocumentTypeData,
    task,
  } = useConfigurationLicence();

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.applicationProcessTypes,
        EDictionaryTypes.applicationProcessStatus,
        EDictionaryTypes.attachmentDocumentTypeData,
        EDictionaryTypes.task,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <>

      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('licence.applicationProcess.processType.label')}
          </Typography>
          <Typography variant="body2">
            {applicationProcess?.processTypeUniqueId
              ? findInDictionary(
                applicationProcess?.processTypeUniqueId,
                applicationProcessTypes,
              )
              : '-'}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('licence.applicationProcess.relatedLegalEntityType.label')}
          </Typography>
          <Typography variant="body2">{applicationProcess?.legalEntityType}</Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('licence.applicationProcess.status.label')}
          </Typography>
          <Typography variant="body2">
            {applicationProcess?.statusUniqueId ? (
              <Chips
                text={findInDictionary(
                  applicationProcess?.statusUniqueId,
                  applicationProcessStatus,
                )}
                variant={statusApplicationProcessChipVariant(applicationProcess.statusUniqueId)}
              />
            ) : (
              ''
            )}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('licence.applicationProcess.comment.label')}
          </Typography>
          <Typography variant="body2">
            {emptyFieldHolder(applicationProcess?.comment)}
          </Typography>
        </Stack>
      </Grid>

      <StyledDivider />

      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('licence.applicationProcess.priceName.label')}
          </Typography>
          <Typography variant="body2">
            {emptyFieldHolder(applicationProcess?.priceName)}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('licence.applicationProcess.price.label')}
          </Typography>
          <Typography variant="body2">
            {`${priceFormat(applicationProcess?.priceFee)} ${currency}`}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('licence.applicationProcess.priceDescription.label')}
          </Typography>
          <Typography variant="body2">
            {getHtmlText(applicationProcess?.priceDescription)}
          </Typography>
        </Stack>
      </Grid>

      <StyledDivider />

      {!!documents && !!documents.length && (
        <Grid sx={{ gap: 1 }}>
          <Typography variant="subtitle2"> {t('licence.documents.label')} </Typography>
          <Table
            columns={ApplicationProcessDocumentTableColumns({})}
            dataSource={mapApplicationProcessDocument(documents, attachmentDocumentTypeData)}
          />
        </Grid>
      )}
      {!!tasks && !!tasks.length && (
        <>
          {!!documents && !!documents.length && <StyledDivider />}
          <Grid sx={{ gap: 1 }}>
            <Typography variant="subtitle2"> {t('licence.tasks.label')} </Typography>
            <Table
              columns={ApplicationProcessTaskTableColumns({})}
              dataSource={mapApplicationProcessTask(tasks, task)}
            />
          </Grid>
        </>
      )}
    </>
  );
};

const LicencesApplicationProcessEventDetails = ({ auditLog }: ILicencesEventDetails) => {
  const [applicationProcess, setApplicationProcess] =
    useState<ILicenceTemplateApplicationProcessResponse | null>();
  const [documents, setDocument] = useState<ILicenceTemplateApplicationProcessDocument[]>([]);
  const [tasks, setTasks] = useState<ILicenceTemplateApplicationProcessTask[]>([]);
  const [previousApplicationProcess, setPreviousApplicationProcess] =
    useState<ILicenceTemplateApplicationProcessResponse | null>();
  const [previousDocuments, setPreviousDocument] = useState<
    ILicenceTemplateApplicationProcessDocument[]
  >([]);
  const [previousTasks, setPreviousTasks] = useState<ILicenceTemplateApplicationProcessTask[]>(
    [],
  );
  const { t } = useTranslations();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (auditLog && auditLog?.postObjectValue) {
        const postObjectValue = auditLog.postObjectValue as any;
        if (auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_APPLICATION_PROCESS) {
          setApplicationProcess(postObjectValue.general);
          setDocument(postObjectValue.documents);
          setTasks(postObjectValue.tasks);
        } else {
          setApplicationProcess(postObjectValue);
        }
      }
      if (auditLog && auditLog?.previousObjectValue) {
        const previousObjectValue = auditLog.previousObjectValue as any;
        if (auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_APPLICATION_PROCESS) {
          setPreviousApplicationProcess(previousObjectValue.general);
          setPreviousDocument(previousObjectValue.documents);
          setPreviousTasks(previousObjectValue.tasks);
        } else {
          setPreviousApplicationProcess(previousObjectValue);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditLog]);

  return (
    <>
      {previousApplicationProcess && (
        <AuditLogEventName>
          <Typography variant="h5">
            {auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_APPLICATION_PROCESS
              ? `${t('applicationProcess.header')} ${t('auditLogDetails.before.event.title')}`
              : `${t('applicationProcess.header')} ${t('auditLogDetails.after.event.title')}`}
          </Typography>
        </AuditLogEventName>
      )}

      {previousApplicationProcess && (
        <DetailsTemplate
          title={previousApplicationProcess?.applicationProcessId}
          pageType={
            auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LICENCE_DEFINITION
              ? EDetailsPageTypes.auditLogView
              : undefined
          }
          disableActions={
            auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_APPLICATION_PROCESS
          }
          redirectToView={() =>
            navigate(
              `${ERouteLinks.LicenceTemplateApplicationProcessPage.replace(
                ':id',
                previousApplicationProcess.licenceTemplateUniqueId ?? '',
              ).replace(':processId', previousApplicationProcess.uniqueId)}`,
            )
          }
        >
          <RenderDetails
            applicationProcess={previousApplicationProcess}
            t={t}
            documents={previousDocuments}
            tasks={previousTasks}
          />
        </DetailsTemplate >
      )}

      {
        auditLog && (
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {`${t('applicationProcess.header')} ${t('auditLogDetails.post.event.title')}`}
            </Typography>
          </AuditLogEventName>
        )
      }
      {
        applicationProcess && (
          <DetailsTemplate
            title={applicationProcess?.applicationProcessId}
            pageType={
              auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LICENCE_DEFINITION
                ? EDetailsPageTypes.auditLogView : undefined
            }
            redirectToView={() =>
              navigate(
                `${ERouteLinks.LicenceTemplateApplicationProcessPage.replace(
                  ':id',
                  applicationProcess.licenceTemplateUniqueId ?? '',
                ).replace(':processId', applicationProcess.uniqueId)}`,
              )
            }
          >
            <RenderDetails
              applicationProcess={applicationProcess}
              t={t}
              documents={documents}
              tasks={tasks}
            />
          </DetailsTemplate >
        )}
    </>
  );
};

export default LicencesApplicationProcessEventDetails;
